/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";

import BookCardItem from "../../components/bookCardItem";
import BookListItem from "../../components/bookListItem";
import BookCoverItem from "../../components/bookCoverItem";
import MultipleSelector from "../../components/multipleSelector";
import LoadingSkeleton from "../../features/books/BookSkeleton";
import DownloadingLoader from "../../components/loading/DownloadingLoader";
import EmptyPage from "../../pages/emptyPage/component";
import ShelfUtil from "../../utils/readUtils/shelfUtil";
import BookModel from "../../models/Book";
import { useDownloadingState } from "../../hooks/useDownloadingState";

import { BookListProps } from "./interface";
import StorageUtil from "../../utils/serviceUtils/storageUtil";
import { fetchMD5 } from "../../utils/fileUtils/md5Util";
import { useHistory, useLocation } from "react-router-dom";
import BookUtil from "../../utils/fileUtils/bookUtil";
import RecordRecent from "../../utils/readUtils/recordRecent";

import toast from "react-hot-toast";
import axios from "axios";
import api from "../../utils/axios";

import Manager from "../../pages/manager";
import authService, { UserData } from "../../utils/authService";

declare var window: any;
let clickFilePath = "";

const shelfList = ["Comics", "Storybooks", "History", "Moral Education", "Biographies", "Popular Science"];
const languageList = ["Malay", "English", "Mandarin"];

const BookList: React.FC<BookListProps> = (props) => {
  const userData: UserData | null = authService.getUserData();
  const user_id = userData?.id;
  const userIc = userData?.ic_number;

  const history = useHistory();
  const location = useLocation();
  const [favoriteBooks, setFavoriteBooks] = useState<string[]>([]);
  const [isOpenFile, setIsOpenFile] = useState(false);
  const [books, setBooks] = useState<any[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  // Downloading state management
  const { downloadingState, startDownloading, updateProgress, finishDownloading, cancelDownloading } =
    useDownloadingState();

  const queryParams = new URLSearchParams(location.search);
  const selectedShelves = queryParams.get("genres")?.split(",") || shelfList;
  const selectedLanguages = queryParams.get("lang")?.split(",") || languageList;
  const currentPage = parseInt(queryParams.get("page") || "1");
  const pageSize = parseInt(queryParams.get("pageSize") || "24");
  const genres = queryParams.get("genres") || shelfList.join(",");
  const lang = queryParams.get("lang") || languageList.join(",");
  const order = queryParams.get("order") || "ASC";
  const orderBy = queryParams.get("orderBy") || "title";
  const keyword = queryParams.get("keyword") || "";

  const loadBookList = async () => {
    const { mode } = props;
    setBooks([]);
    props.handleLoadingBook(true);

    try {
      const response = await api.get(
        `/api/ebooks/list?page=${currentPage}&limit=${pageSize}&keyword=${keyword}&order=${order}&orderBy=${orderBy}&mode=${mode}&user_id=${userIc}&lang=${lang}&genres=${genres}`
      );

      const books = response.data.data.map((book: any) => ({
        ...book,
        key: book.id,
        format: book.title.split(".").pop(),
        name: book.title,
        source_url: book.url,
        thumb_url: book.thumb_url,
        size: book.size,
        upload_time: book.upload_time,
      }));

      setTotalItems(response.data.total);
      setBooks(books);

      window.localforage.setItem("books", books).then(() => {
        props.handleFetchBooks();
      });
    } catch (error) {
      console.error("[BookList] Error loading books:", error);
      toast.error(props.t("Failed to load books"));
    } finally {
      props.handleLoadingBook(false);
    }
  };

  const loadHighlights = async () => {
    try {
      const response = await api.get(`/api/highlights/getByUserIC/${userIc}`);
      const noteArr = response.data.data;
      window.localforage.setItem("notes", noteArr);
    } catch (error) {
      console.error("[BookList] Error loading highlights:", error);
    }
  };

  const loadFavoriteBooks = async () => {
    try {
      const response = await api.get(`/api/users/favorites/${user_id}`);
      const favoriteBooks = response.data.data;
      setFavoriteBooks(favoriteBooks);
    } catch (error) {
      console.error("[BookList] Error loading favorite books:", error);
    }
  };

  const getFileFormat = (filename: string) => {
    const parts = filename.split(".");
    const format = parts.length > 1 ? parts[parts.length - 1] : "";
    let type = "application/pdf";
    if (format === "epub") type = "application/epub+zip";
    return { format, type };
  };

  const lazyLoad = () => {
    const lazyImages: any = document.querySelectorAll(".lazy-image");
    lazyImages.forEach((lazyImage: any) => {
      if (isElementInViewport(lazyImage) && lazyImage.dataset.src) {
        lazyImage.src = lazyImage.dataset.src;
        lazyImage.classList.remove("lazy-image");
      }
    });
  };

  const isElementInViewport = (element: any) => {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  };

  const loadContentBook = async (book: BookModel) => {
    // Start downloading state
    startDownloading(book.name || props.t("Unknown Book"), book.key);
    updateProgress(10);

    try {
      await new Promise<void>((resolve, reject) => {
        book.source_url = book.source_url.replace(/_/g, "+");
        updateProgress(20);

        axios
          .get<ArrayBuffer>(book.source_url, {
            responseType: "arraybuffer",
            onDownloadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                updateProgress(Math.min(90, 20 + progress * 0.7)); // Scale to 20-90%
              }
            },
          })
          .then(async (res) => {
            updateProgress(90);
            const arrayBuffer = res.data;
            const { type, format } = getFileFormat(book.file_key);
            const blob = new Blob([new Uint8Array(arrayBuffer)], { type });
            const file = new File([blob], `${book.name}.${format}`, { type });

            await getMd5WithBrowser(
              file,
              book.file_key,
              book.thumbnail,
              book.thumb_url,
              book.source_url,
              book.key,
              book.assistant_id,
              book.language
            );
            updateProgress(100);

            resolve();
            finishDownloading();
          })
          .catch((error) => {
            console.error("Error downloading book:", error);
            cancelDownloading();
            reject(error);
          });
      });
    } catch (error) {
      console.error("Error in loadContentBook:", error);
      cancelDownloading();
      throw error;
    }

    return book.key;
  };

  const handleJump = (book: BookModel) => {
    localStorage.setItem("tempBook", JSON.stringify(book));
    BookUtil.RedirectBook(book, props.t, history);
  };

  const handleToggleFavorite = async (book: any) => {
    try {
      const isCurrentlyFavorite = favoriteBooks.includes(book.key);

      if (isCurrentlyFavorite) {
        // Remove from favorites
        await api.post(`/api/users/remove-favorite`, {
          user_id: user_id,
          book_id: book.key,
        });
        setFavoriteBooks((prev) => prev.filter((key) => key !== book.key));
        toast.success(props.t("Removed from favorites"));
      } else {
        // Add to favorites
        await api.post(`/api/users/add-favorite`, {
          user_id: user_id,
          book_id: book.key,
        });
        setFavoriteBooks((prev) => [...prev, book.key]);
        toast.success(props.t("Added to favorites"));
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error(props.t("Failed to update favorites"));
    }
  };

  const handleAddBook = async (book: BookModel, buffer: ArrayBuffer) => {
    return new Promise<void>((resolve, reject) => {
      if (isOpenFile) {
        StorageUtil.getReaderConfig("isImportPath") !== "yes" &&
          StorageUtil.getReaderConfig("isPreventAdd") !== "yes" &&
          BookUtil.addBook(book.key, buffer);
        if (StorageUtil.getReaderConfig("isPreventAdd") === "yes") {
          handleJump(book);
          setIsOpenFile(false);
          return resolve();
        }
      } else {
        StorageUtil.getReaderConfig("isImportPath") !== "yes" && BookUtil.addBook(book.key, buffer);
      }

      let bookArr = [...(props.books || []), ...props.deletedBooks];
      if (bookArr == null) {
        bookArr = [];
      }

      let index = bookArr.findIndex((b) => b.file_key === book.file_key);
      if (index !== -1) {
        bookArr[index] = book;
      } else {
        bookArr.push(book);
      }

      props.handleReadingBook(book);
      RecordRecent.setRecent(book.key);
      window.localforage
        .setItem("books", bookArr)
        .then(() => {
          props.handleFetchBooks();
          if (props.mode === "shelf") {
            let shelfTitles = Object.keys(ShelfUtil.getShelf());
            ShelfUtil.setShelf(shelfTitles[props.shelfIndex], book.key);
          }
          setTimeout(() => {
            isOpenFile && handleJump(book);
            if (StorageUtil.getReaderConfig("isOpenInMain") === "yes" && isOpenFile) {
              setIsOpenFile(false);
              return;
            }
            setIsOpenFile(false);
          }, 100);
          return resolve();
        })
        .catch(() => {
          toast.error(props.t("Import failed"));
          return resolve();
        });
    });
  };

  const getMd5WithBrowser = async (
    file: any,
    file_key: string = "",
    thumbnail: string = "",
    thumb_url: string = "",
    source_url: string = "",
    key: string = "",
    assistant_id: string = "",
    language: string = ""
  ) => {
    return new Promise<void>(async (resolve, reject) => {
      const md5 = await fetchMD5(file);
      if (!md5) {
        toast.error(props.t("Import failed"));
        return resolve();
      } else {
        try {
          await handleBook(file, md5, file_key, thumbnail, thumb_url, source_url, key, assistant_id, language);
        } catch (error) {
          // Handle error silently
        }
        return resolve();
      }
    });
  };

  const handleBook = (
    file: any,
    md5: string,
    file_key: string,
    thumbnail: string,
    thumb_url: string,
    source_url: string,
    key: string = "",
    assistant_id: string = "",
    language: string = ""
  ) => {
    let extension = (file.name as string).split(".").reverse()[0].toLocaleLowerCase();
    let bookName = file.name.substr(0, file.name.length - extension.length - 1);
    let result: BookModel | string;
    return new Promise<void>((resolve, reject) => {
      let isRepeat = false;
      if (props.books.length > 0) {
        props.books.forEach((item) => {
          if (item.md5 === md5 && item.size === file.size) {
            isRepeat = true;
            if (!key) return resolve();
          }
        });
      }
      if (props.deletedBooks.length > 0) {
        props.deletedBooks.forEach((item) => {
          if (item.md5 === md5 && item.size === file.size) {
            isRepeat = true;
            toast.error(props.t("Duplicate book in trash bin"));
            if (!key) return resolve();
          }
        });
      }
      if (!isRepeat || !!key) {
        let reader = new FileReader();
        reader.readAsArrayBuffer(file);

        reader.onload = async (e) => {
          if (!e.target) {
            toast.error(props.t("Import failed"));
            return resolve();
          }
          let reader = new FileReader();
          reader.onload = async (event) => {
            const file_content = (event.target as any).result;
            try {
              result = await BookUtil.generateBook(
                bookName,
                extension,
                md5,
                file.size,
                file.path || clickFilePath,
                file_content,
                file_key,
                thumbnail,
                thumb_url,
                source_url,
                key,
                assistant_id,
                language
              );
              if (result === "get_metadata_error") {
                toast.error(props.t("Import failed"));
                return resolve();
              }
              if (!!key) {
                (result as BookModel).key = key;
              }
            } catch (error) {
              throw error;
            }

            clickFilePath = "";
            await handleAddBook(result as BookModel, file_content as ArrayBuffer);

            return resolve();
          };
          reader.readAsArrayBuffer(file);
        };
      }
    });
  };

  const updateUrl = (
    selectedShelves: string[],
    selectedLanguages: string[],
    currentPage: number,
    pageSize: number,
    order: string = "asc",
    orderBy: string = "title"
  ) => {
    const searchParams = new URLSearchParams(location.search);

    // Update only the specified parameters
    if (selectedShelves.length > 0) {
      searchParams.set("genres", selectedShelves.join(","));
    } else {
      searchParams.delete("genres");
    }

    if (selectedLanguages.length > 0) {
      searchParams.set("lang", selectedLanguages.join(","));
    } else {
      searchParams.delete("lang");
    }

    searchParams.set("page", currentPage.toString());
    searchParams.set("pageSize", pageSize.toString());

    if (order) {
      searchParams.set("order", order);
    }
    if (orderBy) {
      searchParams.set("orderBy", orderBy);
    }

    if (keyword) {
      searchParams.set("keyword", keyword);
    } else {
      searchParams.delete("keyword");
    }

    const queryString = searchParams.toString();
    history.replace({
      pathname: location.pathname,
      search: queryString ? `?${queryString}` : "",
      hash: location.hash,
    });

    // Call loadBookList after URL and state updates
  };

  const renderBookList = () => {
    const bookItems = books;
    setTimeout(() => {
      lazyLoad();
    }, 0);
    let listElements = document.querySelector(".book-list-item-box");
    let covers = listElements?.querySelectorAll("img");
    covers?.forEach((cover) => {
      if (!cover.classList.contains("lazy-image")) {
        cover.classList.add("lazy-image");
      }
    });
    return (
      <React.Fragment>
        {bookItems &&
          bookItems.map((item: any, index: number) => {
            const isFavorite = favoriteBooks.includes(item.key);
            return props.viewMode === "list" ? (
              <BookListItem
                {...{
                  key: index,
                  book: item,
                  loadContentBook,
                  loadBookList,
                  isSelected: props.selectedBooks.indexOf(item.key) > -1,
                  isFavorite,
                }}
              />
            ) : props.viewMode === "card" ? (
              <BookCardItem
                key={index}
                book={item}
                isFavorite={isFavorite}
                t={props.t}
                handleReadingBook={props.handleReadingBook}
                loadContentBook={loadContentBook}
                onToggleFavorite={handleToggleFavorite}
              />
            ) : (
              <BookCoverItem
                {...{
                  key: index,
                  book: item,
                  loadContentBook,
                  loadBookList,
                  isSelected: props.selectedBooks.indexOf(item.key) > -1,
                  isFavorite,
                }}
              />
            );
          })}
      </React.Fragment>
    );
  };

  useEffect(() => {
    loadHighlights();
  }, []);

  useEffect(() => {
    loadFavoriteBooks();
  }, []);

  useEffect(() => {
    loadBookList();
  }, [order, orderBy, currentPage, keyword, genres, lang]);

  let bookListContent;
  if (!props.books || !props.books[0]) {
    bookListContent = <EmptyPage mode="empty" isCollapsed={false} />;
  } else {
    bookListContent = (
      <>
        <div className="relative h-full rounded-xl p-4 ">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-6 flex justify-between">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {Math.min((currentPage - 1) * pageSize + 1, totalItems)}
                </span>{" "}
                -{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {Math.min(currentPage * pageSize, totalItems)}
                </span>{" "}
                {props.t("of")} <span className="font-medium text-gray-900 dark:text-gray-100">{totalItems}</span>
              </span>

              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    const newPage = currentPage - 1;
                    if (newPage >= 1) {
                      updateUrl(selectedShelves, selectedLanguages, newPage, pageSize, order, orderBy);
                    }
                  }}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-white dark:disabled:hover:border-gray-600 dark:disabled:hover:bg-gray-700 shadow-sm"
                  aria-label={props.t("Previous page")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <div className="flex items-center gap-1">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{currentPage}</span>
                  <span className="text-gray-500 dark:text-gray-400">/</span>
                  <span className="text-gray-500 dark:text-gray-400">{Math.ceil(totalItems / pageSize)}</span>
                </div>
                <button
                  onClick={() => {
                    const newPage = currentPage + 1;
                    if (newPage <= Math.ceil(totalItems / pageSize)) {
                      updateUrl(selectedShelves, selectedLanguages, newPage, pageSize, order, orderBy);
                    }
                  }}
                  disabled={currentPage >= Math.ceil(totalItems / pageSize)}
                  className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:bg-white dark:disabled:hover:border-gray-600 dark:disabled:hover:bg-gray-700 shadow-sm"
                  aria-label={props.t("Next page")}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
              <select
                value={`${orderBy}-${order}`}
                onChange={(e) => {
                  const [newOrderBy, newOrder] = e.target.value.split("-");
                  updateUrl(selectedShelves, selectedLanguages, currentPage, pageSize, newOrder, newOrderBy);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              >
                <option value="title-asc">{props.t("Title (A-Z)")}</option>
                <option value="title-desc">{props.t("Title (Z-A)")}</option>
              </select>
            </div>
            <div className="flex flex-row gap-3">
              <MultipleSelector
                options={shelfList}
                selected={selectedShelves}
                onApply={(newSelected) =>
                  updateUrl(newSelected, selectedLanguages, currentPage, pageSize, order, orderBy)
                }
                label="Shelves"
                title="Select Shelves"
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                }
                t={props.t}
              />
              <MultipleSelector
                options={languageList}
                selected={selectedLanguages}
                onApply={(newSelected) =>
                  updateUrl(selectedShelves, newSelected, currentPage, pageSize, order, orderBy)
                }
                label="languages"
                title="Select Languages"
                icon={
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                    />
                  </svg>
                }
                t={props.t}
              />
            </div>
          </div>

          {props.isLoadingBook && (
            <div className=" w-full">
              <LoadingSkeleton />
            </div>
          )}
          <ul
            className="bg-white dark:bg-gray-800 w-full h-[calc(100vh_-_180px)] overflow-auto grid 2xl:grid-cols-6 xl:grid-cols-5 lg:grid-cols-4 md:grid-cols-3 grid-cols-2 gap-2 justify-items-center rounded-xl"
            onScroll={() => {
              lazyLoad();
            }}
          >
            {renderBookList()}
          </ul>
        </div>
        {/* <div className="book-list-header">
          <SelectBook />
          <div style={props.isSelectBook ? { display: "none" } : {}}>
            <ViewMode />
          </div>
        </div> */}
      </>
    );
  }

  StorageUtil.setReaderConfig("totalBooks", props.books?.length?.toString());

  return (
    <Manager>
      {bookListContent}
      <DownloadingLoader
        isVisible={downloadingState.isDownloading}
        bookTitle={downloadingState.bookTitle}
        progress={downloadingState.progress}
        onCancel={cancelDownloading}
      />
    </Manager>
  );
};

export default BookList;
